import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";

// ── AI Cost Types ──────────────────────────────────────────────────────────

// TODO: backend endpoint GET superadmin/ai-cost-summary needs to be created in velaro-messaging
// returning AiCostRow[] (one row per site, pulling from Subscription columns added in AddAiBlockPricingAndCostTracking migration)
interface AiCostRow {
  siteId: number;
  companyName: string;
  aiConversationsUsed: number;
  maxAiConversations: number;
  blockBalance: number;
  aiCostCentsThisMonth: number;
  aiCostAlertThreshold: number;
  defaultAiModel: string;
  enableByokAi: boolean;
}

// ── Types ──────────────────────────────────────────────────────────────────

interface SiteSummary {
  siteId: number;
  companyName: string;
  agentCount: number;
  aiCallsMonth: number;
  agentLogins: number;
  uniqueLoginAgents: number;
  adminActions: number;
  adoptionScore: number;
  healthScore: number;
  healthStatus: "healthy" | "watch" | "at-risk" | "critical";
  daysSinceActive: number | null;
  lastActiveAt: string | null;
  chatsMonth: number;
  missedChats: number;
  missedPct: number;
  deflectionPct: number;
  avgResponseSecs: number;
  hasAI: boolean;
  hasAutomation: boolean;
  hasSms: boolean;
  hasHubSpot: boolean;
}

interface Overview {
  generatedAt: string;
  totalSites: number;
  criticalCount: number;
  atRiskCount: number;
  watchCount: number;
  healthyCount: number;
  sites: SiteSummary[];
}

interface AgentDetail {
  id: number;
  name: string;
  email: string;
  status: string;
  licenseStatus: string;
  maxChats: number;
  loginsThisMonth: number;
  lastActiveAt: string | null;
}

interface FeatureDetail {
  feature: string;
  enabled: boolean;
}

interface ConversationMetrics {
  totalThisMonth: number;
  missed: number;
  missedPct: number;
  aiResolved: number;
  deflectionPct: number;
  avgResponseSecs: number;
}

interface CrmStats {
  companies: number;
  openDeals: number;
  wonDealsMonth: number;
  openPipelineValue: number;
  wonRevenueMonth: number;
  topStages: { stage: string; count: number; value: number }[];
  isUsingCrm: boolean;
}

interface SiteDetail {
  siteId: number;
  companyName: string;
  generatedAt: string;
  healthScore: number;
  healthStatus: string;
  daysSinceActive: number | null;
  lastActiveAt: string | null;
  agents: { total: number; active: number; list: AgentDetail[] };
  aiUsage: { totalCallsThisMonth: number; topTools: { tool: string; calls: number; success: number }[] };
  conversations: ConversationMetrics;
  agentLogins: { byWeek: { label: string; count: number; unique: number }[]; recent: { userEmail: string; loggedInAt: string }[] };
  adminActivity: { byCategory: { category: string; count: number }[]; recent: { category: string; action: string; entityName: string; userEmail: string; createdAt: string }[] };
  features: { adoptionScore: number; totalFeatures: number; list: FeatureDetail[] };
  crm?: CrmStats;
}

// ── Status helpers ─────────────────────────────────────────────────────────

const STATUS_STYLES = {
  healthy:  { bar: "bg-green-500",  badge: "bg-green-100 text-green-700 border-green-200",   dot: "w-2 h-2 rounded-full bg-green-500" },
  watch:    { bar: "bg-amber-400",  badge: "bg-amber-100 text-amber-700 border-amber-200",   dot: "w-2 h-2 rounded-full bg-amber-400" },
  "at-risk":{ bar: "bg-orange-500", badge: "bg-orange-100 text-orange-700 border-orange-200",dot: "w-2 h-2 rounded-full bg-orange-500" },
  critical: { bar: "bg-red-500",    badge: "bg-red-100 text-red-700 border-red-200",         dot: "w-2 h-2 rounded-full bg-red-500" },
} as const;

function StatusBadge({ status }: { status: SiteSummary["healthStatus"] }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${s.badge}`}>
      {status.toUpperCase()}
    </span>
  );
}

function ScoreBar({ score }: { score: number }) {
  const color = score >= 70 ? "bg-green-500" : score >= 40 ? "bg-amber-400" : score >= 20 ? "bg-orange-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
        <div className={`${color} h-1.5 rounded-full`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-mono font-semibold text-gray-700 w-8 text-right">{score}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-800">{value}</span>
    </div>
  );
}

// ── Site detail drawer ─────────────────────────────────────────────────────

function SiteDetailPanel({ siteId, onClose }: { siteId: number; onClose: () => void }) {
  const api = useApi();
  const [detail, setDetail] = useState<SiteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"overview" | "agents" | "activity" | "features" | "crm">("overview");

  useEffect(() => {
    setLoading(true);
    setDetail(null);
    api.admin.get(`AccountHealth/site/${siteId}`)
      .then(r => r.json())
      .then(d => setDetail(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const TABS = ["overview", "agents", "activity", "features", "crm"] as const;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {detail?.companyName ?? `Site ${siteId}`}
            </div>
            <div className="text-xs text-gray-500">Site ID: {siteId}</div>
          </div>
          <div className="flex items-center gap-3">
            {detail && <StatusBadge status={detail.healthStatus as SiteSummary["healthStatus"]} />}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl font-bold">×</button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>
        )}

        {detail && (
          <>
            {/* Score banner */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Health Score</div>
                  <ScoreBar score={detail.healthScore} />
                </div>
                <Stat label="Agents" value={`${detail.agents.active} active / ${detail.agents.total} total`} />
                <Stat label="AI Calls (month)" value={detail.aiUsage.totalCallsThisMonth.toLocaleString()} />
                {detail.daysSinceActive != null
                  ? <Stat label="Last Active" value={`${detail.daysSinceActive}d ago`} />
                  : <Stat label="Last Active" value="Never" />}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b">
              {TABS.map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-5 py-3 text-sm capitalize font-medium border-b-2 transition-colors ${
                    tab === t ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="px-6 py-4">

              {/* ── Overview ── */}
              {tab === "overview" && (
                <div className="space-y-4">
                  {/* Conversation metrics */}
                  {detail.conversations && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Conversations This Month</h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gray-50 rounded p-3 text-center">
                          <div className="text-lg font-bold text-gray-800">{detail.conversations.totalThisMonth.toLocaleString()}</div>
                          <div className="text-xs text-gray-500">Total Chats</div>
                        </div>
                        <div className="bg-gray-50 rounded p-3 text-center">
                          <div className="text-lg font-bold text-orange-600">{detail.conversations.missedPct}%</div>
                          <div className="text-xs text-gray-500">Missed ({detail.conversations.missed})</div>
                        </div>
                        <div className="bg-gray-50 rounded p-3 text-center">
                          <div className="text-lg font-bold text-green-600">{detail.conversations.deflectionPct}%</div>
                          <div className="text-xs text-gray-500">AI Deflection</div>
                        </div>
                      </div>
                      {detail.conversations.avgResponseSecs > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          Avg first response: {Math.round(detail.conversations.avgResponseSecs)}s
                        </div>
                      )}
                    </div>
                  )}

                  {/* Top AI tools */}
                  {detail.aiUsage.topTools.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Top AI Tools This Month</h3>
                      <div className="space-y-1">
                        {detail.aiUsage.topTools.map(t => (
                          <div key={t.tool} className="flex items-center gap-2 text-sm">
                            <span className="flex-1 text-gray-700 font-mono text-xs truncate">{t.tool}</span>
                            <span className="text-gray-500">{t.calls.toLocaleString()} calls</span>
                            <span className="text-green-600 text-xs">
                              {t.calls > 0 ? Math.round(t.success / t.calls * 100) : 0}% ok
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Login trend */}
                  {detail.agentLogins.byWeek.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Agent Logins by Week</h3>
                      <div className="flex items-end gap-1 h-16">
                        {detail.agentLogins.byWeek.map(w => {
                          const maxVal = Math.max(...detail.agentLogins.byWeek.map(x => x.count), 1);
                          const pct    = Math.round(w.count / maxVal * 100);
                          return (
                            <div key={w.label} className="flex-1 flex flex-col items-center gap-0.5" title={`${w.label}: ${w.count} logins, ${w.unique} agents`}>
                              <div className="w-full bg-blue-200 rounded-t" style={{ height: `${pct}%`, minHeight: w.count > 0 ? "4px" : "0" }} />
                              <span className="text-[9px] text-gray-400">{w.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Admin activity by category */}
                  {detail.adminActivity.byCategory.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Admin Activity This Month</h3>
                      <div className="flex flex-wrap gap-2">
                        {detail.adminActivity.byCategory.map(c => (
                          <span key={c.category} className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded">
                            {c.category}: {c.count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Agents ── */}
              {tab === "agents" && (
                <div>
                  <div className="text-sm text-gray-500 mb-3">
                    {detail.agents.active} agents active this month out of {detail.agents.total} total
                  </div>
                  <div className="space-y-2">
                    {detail.agents.list.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div>
                          <div className="font-medium text-gray-800">{a.name}</div>
                          <div className="text-xs text-gray-500">{a.email}</div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className={a.status === "Available" ? "text-green-600" : ""}>{a.status}</span>
                          <span>{a.licenseStatus}</span>
                          {a.maxChats > 0 && <span>Max: {a.maxChats}</span>}
                          {a.loginsThisMonth > 0 && (
                            <span className="text-blue-600">{a.loginsThisMonth} logins</span>
                          )}
                          {a.lastActiveAt && (
                            <span title={new Date(a.lastActiveAt).toLocaleString()}>
                              last: {new Date(a.lastActiveAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Activity ── */}
              {tab === "activity" && (
                <div className="space-y-1">
                  {detail.adminActivity.recent.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 py-1.5 border-b border-gray-100 text-sm">
                      <span className="text-xs text-gray-400 mt-0.5 w-28 shrink-0">
                        {new Date(a.createdAt).toLocaleDateString()}
                      </span>
                      <span className="bg-slate-100 text-slate-600 text-xs px-1.5 py-0.5 rounded shrink-0">{a.category}</span>
                      <span className="text-gray-700 truncate">{a.action}{a.entityName ? ` — ${a.entityName}` : ""}</span>
                      <span className="text-gray-400 text-xs shrink-0 ml-auto">{a.userEmail}</span>
                    </div>
                  ))}
                  {detail.adminActivity.recent.length === 0 && (
                    <p className="text-sm text-gray-500">No activity recorded this month.</p>
                  )}
                </div>
              )}

              {/* ── Features ── */}
              {tab === "features" && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-gray-700">
                      {detail.features.adoptionScore} / {detail.features.totalFeatures} features enabled
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full"
                        style={{ width: `${Math.round(detail.features.adoptionScore / detail.features.totalFeatures * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {detail.features.list.map(f => (
                      <div key={f.feature} className={`flex items-center gap-2 text-sm p-2 rounded ${f.enabled ? "bg-green-50 text-green-800" : "bg-gray-50 text-gray-400"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${f.enabled ? "bg-green-500" : "bg-gray-300"}`} />
                        {f.feature}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── CRM ── */}
              {tab === "crm" && (
                <div>
                  {!detail.crm?.isUsingCrm ? (
                    <p className="text-sm text-gray-400 italic">Native CRM not in use for this site.</p>
                  ) : (
                    <div className="space-y-4">
                      {/* Pipeline KPIs */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded p-3">
                          <div className="text-2xl font-bold text-gray-800">{detail.crm.openDeals}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Open Deals</div>
                          <div className="text-xs text-blue-600 font-medium">
                            ${detail.crm.openPipelineValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} pipeline
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <div className="text-2xl font-bold text-green-700">{detail.crm.wonDealsMonth}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Won This Month</div>
                          <div className="text-xs text-green-600 font-medium">
                            ${detail.crm.wonRevenueMonth.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} revenue
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <div className="text-2xl font-bold text-gray-800">{detail.crm.companies}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Companies</div>
                        </div>
                      </div>

                      {/* Pipeline stage breakdown */}
                      {detail.crm.topStages.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Pipeline by Stage</h3>
                          <div className="space-y-1.5">
                            {detail.crm.topStages.map(s => (
                              <div key={s.stage} className="flex items-center gap-2 text-sm">
                                <span className="flex-1 text-gray-700 truncate">{s.stage}</span>
                                <span className="text-gray-500">{s.count} deals</span>
                                {s.value > 0 && (
                                  <span className="text-blue-600 text-xs">
                                    ${s.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── AI Cost Tab ────────────────────────────────────────────────────────────

function AiCostTab() {
  const api = useApi();
  const [rows, setRows]     = useState<AiCostRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // TODO: backend endpoint GET superadmin/ai-cost-summary must be created in velaro-messaging
    api.messaging.get("superadmin/ai-cost-summary")
      .then(r => r.json())
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-gray-500">Loading AI cost data…</div>;
  }

  if (rows.length === 0) {
    return <div className="text-center py-16 text-gray-400">No AI cost data available. Backend endpoint may not be implemented yet.</div>;
  }

  const fmtDollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  return (
    <div>
      <div className="mb-4 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded px-3 py-2">
        Internal use only — actual Velaro AI provider costs per site this month.
        Alert threshold is set per site in Subscription settings (AiCostAlertThresholdCents).
      </div>
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-3 text-left">Company</th>
              <th className="px-4 py-3 text-left">Model</th>
              <th className="px-4 py-3 text-right">AI Usage</th>
              <th className="px-4 py-3 text-right">Block Balance</th>
              <th className="px-4 py-3 text-right">Cost This Month</th>
              <th className="px-4 py-3 text-center">BYOK</th>
              <th className="px-4 py-3 text-center">Alert</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isAlert = row.aiCostAlertThreshold > 0 && row.aiCostCentsThisMonth > row.aiCostAlertThreshold;
              const usagePct = row.maxAiConversations > 0
                ? Math.min(100, Math.round(row.aiConversationsUsed / row.maxAiConversations * 100))
                : 0;
              const usageColor = usagePct >= 90 ? "bg-red-500" : usagePct >= 70 ? "bg-amber-400" : "bg-green-500";

              return (
                <tr key={row.siteId} className={`border-b transition-colors ${isAlert ? "bg-red-50" : "hover:bg-gray-50"}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{row.companyName}</div>
                    <div className="text-xs text-gray-400">#{row.siteId}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-600">
                    {row.defaultAiModel || "platform default"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-20 bg-gray-200 rounded-full h-1.5">
                        <div className={`${usageColor} h-1.5 rounded-full`} style={{ width: `${usagePct}%` }} />
                      </div>
                      <span className="text-xs text-gray-700 w-24 text-right">
                        {row.aiConversationsUsed.toLocaleString()} / {row.maxAiConversations.toLocaleString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {row.blockBalance.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-medium text-gray-800">
                    {fmtDollars(row.aiCostCentsThisMonth)}
                    {row.aiCostAlertThreshold > 0 && (
                      <div className="text-[10px] text-gray-400 font-normal">
                        limit: {fmtDollars(row.aiCostAlertThreshold)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {row.enableByokAi && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">BYOK</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isAlert && (
                      <span className="text-xs bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded font-semibold">
                        OVER LIMIT
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

type DashTab = "sites" | "ai-cost";

export default function AccountHealth() {
  const api = useApi();
  const [overview, setOverview]       = useState<Overview | null>(null);
  const [loading, setLoading]         = useState(true);
  const [selectedSite, setSelectedSite] = useState<number | null>(null);
  const [filter, setFilter]           = useState<"all" | "critical" | "at-risk" | "watch" | "healthy">("all");
  const [search, setSearch]           = useState("");
  const [dashTab, setDashTab]         = useState<DashTab>("sites");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await api.admin.get("AccountHealth/overview");
      const data = await res.json();
      setOverview(data);
    } catch {
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filteredSites = (overview?.sites ?? []).filter(s => {
    if (filter !== "all" && s.healthStatus !== filter) return false;
    if (search && !s.companyName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // ── Summary cards ──────────────────────────────────────────────────────

  const cards = overview ? [
    { label: "Critical",  count: overview.criticalCount, color: "text-red-600",    bg: "bg-red-50",    border: "border-red-200",   key: "critical" as const },
    { label: "At Risk",   count: overview.atRiskCount,   color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", key: "at-risk"  as const },
    { label: "Watch",     count: overview.watchCount,    color: "text-amber-600",  bg: "bg-amber-50",  border: "border-amber-200",  key: "watch"    as const },
    { label: "Healthy",   count: overview.healthyCount,  color: "text-green-600",  bg: "bg-green-50",  border: "border-green-200",  key: "healthy"  as const },
  ] : [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Health</h1>
          <p className="text-sm text-gray-500 mt-1">
            Platform-wide account health for Velaro internal use.
            {overview && ` ${overview.totalSites} active sites · refreshed ${new Date(overview.generatedAt).toLocaleTimeString()}`}
          </p>
        </div>
        <button
          onClick={load}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Refresh
        </button>
      </div>

      {/* Dashboard tabs */}
      <div className="flex border-b mb-6">
        {([ ["sites", "Account Health"], ["ai-cost", "AI Cost"] ] as [DashTab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setDashTab(key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              dashTab === key ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* AI Cost tab */}
      {dashTab === "ai-cost" && <AiCostTab />}

      {/* Sites tab content */}
      {dashTab === "sites" && <>

      {/* Summary cards */}
      {overview && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {cards.map(card => (
            <button
              key={card.key}
              onClick={() => setFilter(filter === card.key ? "all" : card.key)}
              className={`${card.bg} ${card.border} border rounded-lg p-4 text-left transition-all ${filter === card.key ? "ring-2 ring-offset-1 ring-blue-400" : "hover:shadow-sm"}`}
            >
              <div className={`text-3xl font-bold ${card.color}`}>{card.count}</div>
              <div className={`text-sm font-medium ${card.color} mt-1`}>{card.label}</div>
            </button>
          ))}
        </div>
      )}

      {/* Filter + Search */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="search"
          placeholder="Search company name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        {filter !== "all" && (
          <button
            onClick={() => setFilter("all")}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear filter
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">
          {filteredSites.length} site{filteredSites.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Sites table */}
      {loading && (
        <div className="text-center py-16 text-gray-500">Loading account health data...</div>
      )}

      {!loading && filteredSites.length === 0 && (
        <div className="text-center py-16 text-gray-400">No sites match the current filter.</div>
      )}

      {!loading && filteredSites.length > 0 && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Health</th>
                <th className="px-4 py-3 text-right">Score</th>
                <th className="px-4 py-3 text-right">Agents</th>
                <th className="px-4 py-3 text-right">AI Calls</th>
                <th className="px-4 py-3 text-right">Chats</th>
                <th className="px-4 py-3 text-right">Missed%</th>
                <th className="px-4 py-3 text-right">Deflect%</th>
                <th className="px-4 py-3 text-right">Logins</th>
                <th className="px-4 py-3 text-right">Last Active</th>
                <th className="px-4 py-3 text-right">Features</th>
                <th className="px-4 py-3 text-right">Flags</th>
              </tr>
            </thead>
            <tbody>
              {filteredSites.map(site => (
                <tr
                  key={site.siteId}
                  className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedSite(site.siteId)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{site.companyName}</div>
                    <div className="text-xs text-gray-400">#{site.siteId}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={site.healthStatus} />
                  </td>
                  <td className="px-4 py-3 w-32">
                    <ScoreBar score={site.healthScore} />
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{site.agentCount}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{site.aiCallsMonth.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{(site.chatsMonth ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{site.missedPct ?? 0}%</td>
                  <td className="px-4 py-3 text-right text-gray-700">{site.deflectionPct ?? 0}%</td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {site.agentLogins > 0 ? `${site.agentLogins} (${site.uniqueLoginAgents}u)` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-500 text-xs">
                    {site.daysSinceActive != null ? `${site.daysSinceActive}d ago` : "Never"}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{site.adoptionScore}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {site.hasAI        && <span title="AI" className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded">AI</span>}
                      {site.hasAutomation && <span title="Workflows" className="bg-cyan-100 text-cyan-700 text-[10px] px-1.5 py-0.5 rounded">WF</span>}
                      {site.hasSms       && <span title="SMS" className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded">SMS</span>}
                      {site.hasHubSpot   && <span title="HubSpot" className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0.5 rounded">HS</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Site detail drawer */}
      {selectedSite != null && (
        <SiteDetailPanel
          siteId={selectedSite}
          onClose={() => setSelectedSite(null)}
        />
      )}

      </>}
    </div>
  );
}
